import type { CampaignClass } from '@feathr/blackbox';
import { CampaignLabelMap, ECampaignClass } from '@feathr/blackbox';
import type { TCampaignRecord } from '@feathr/extender/hooks';

export interface IAccessConditions {
  showGoogleAds: boolean;
}

export interface ITypeOption {
  id: CampaignClass | 'monetization';
  name?: string;
}

interface IGetOptionsArgs {
  permissions: TCampaignRecord;
  conditions: IAccessConditions;
}

export function getOptions({ permissions, conditions }: IGetOptionsArgs): ITypeOption[] {
  return (
    [
      ...Object.keys(ECampaignClass)
        .map((friendlyKey): ITypeOption | undefined => {
          const campaignKey = friendlyKey as keyof typeof ECampaignClass;
          const permission = permissions[campaignKey] as boolean | number;
          const realClassName = ECampaignClass[campaignKey] as unknown as CampaignClass;

          // Special conditions for Google Ads campaigns
          if (friendlyKey === 'googleAdsSmart') {
            if (conditions.showGoogleAds && permission) {
              return {
                id: realClassName,
                name: CampaignLabelMap.get(realClassName),
              };
            }
            return undefined;
          }

          // Only return campaign types that are permitted
          // false = disabled, 0 = no quota, other falsy values = disabled
          // true, Infinity, or positive numbers = enabled
          if (permission && permission !== 0) {
            return {
              id: realClassName,
              name: CampaignLabelMap.get(realClassName),
            };
          }
          return undefined;
        })
        .filter((option): option is ITypeOption => option !== undefined),
      // Add monetization option if enabled
      ...(permissions.monetization ? [{ id: 'monetization' as const, name: 'Monetization' }] : []),
    ]
      // Alphabetize options to make searching easier
      .sort((a, b) => {
        if (!a.name) {
          return 1;
        }
        if (!b.name) {
          return -1;
        }
        return a.name.localeCompare(b.name);
      })
  );
}
